import { Reveal, SectionHeading } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n";

const SECTIONS_IT = [
  {
    title: "1. Titolare del trattamento",
    body: [
      "Il titolare del trattamento dei dati personali è Mediolanum Consulting Srl, con sede legale in Via Card. Branda Castiglioni 2/1, 20156 Milano (MI), P.IVA / C.F. 14419200960, REA MI-2781751.",
      "Contatti del titolare: email info@mediolanumconsulting.com — PEC mediocons@pec.it — Tel. +39 0235983003.",
    ],
  },
  {
    title: "2. Dati raccolti e finalità",
    body: [
      "Dati forniti volontariamente: tramite il modulo contatti e il modulo di iscrizione alla newsletter raccogliamo nome, ragione sociale, email, telefono e contenuto del messaggio. Tali dati sono trattati esclusivamente per rispondere alla richiesta, per l'eventuale instaurazione di un rapporto professionale e — solo se richiesto — per l'invio della newsletter Mediolanum Financial Review.",
      "Dati dell'Area Riservata: per i clienti dello studio trattiamo credenziali di accesso, documenti caricati e metadati tecnici, al solo fine di erogare il servizio di portale documentale riservato.",
      "Dati di navigazione: i sistemi informatici acquisiscono alcuni dati tecnici (indirizzo IP, tipo di browser, orario di accesso) necessari al funzionamento e alla sicurezza del sito.",
    ],
  },
  {
    title: "3. Base giuridica del trattamento",
    body: [
      "Il trattamento si fonda sull'esecuzione di misure precontrattuali e contrattuali (art. 6, par. 1, lett. b GDPR), sull'adempimento di obblighi legali (art. 6, par. 1, lett. c GDPR), sul legittimo interesse del titolare alla sicurezza dei sistemi (art. 6, par. 1, lett. f GDPR) e, per la newsletter e i cookie non tecnici, sul consenso dell'interessato (art. 6, par. 1, lett. a GDPR), revocabile in qualsiasi momento.",
    ],
  },
  {
    title: "4. Conservazione e sicurezza",
    body: [
      "I dati sono conservati per il tempo strettamente necessario alle finalità indicate e comunque secondo i termini di legge applicabili agli atti professionali. Le richieste di contatto sono conservate per un massimo di 24 mesi; i documenti dell'Area Riservata per la durata del rapporto professionale.",
      "Adottiamo misure tecniche e organizzative adeguate: connessioni cifrate (HTTPS), credenziali protette con algoritmi di hashing, accesso ai documenti riservato e tracciato, protocolli di riservatezza interni.",
    ],
  },
  {
    title: "5. Comunicazione a terzi",
    body: [
      "I dati non sono venduti né ceduti a terzi per finalità di marketing. Possono essere comunicati esclusivamente a fornitori tecnici che operano come responsabili del trattamento (hosting, servizi email transazionali) e, su espressa richiesta del cliente, a professionisti coinvolti nel mandato (notai, consulenti, istituti di credito).",
    ],
  },
  {
    title: "6. Diritti dell'interessato",
    body: [
      "Ai sensi degli artt. 15-22 del Reg. UE 2016/679 avete diritto di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al trattamento, nonché di revoca del consenso prestato.",
      "Per esercitare i diritti scrivete a info@mediolanumconsulting.com o alla PEC mediocons@pec.it. Avete inoltre diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).",
    ],
  },
  {
    title: "7. Cookie Policy",
    body: [
      "Cookie tecnici (necessari): indispensabili per il funzionamento del sito e dell'Area Riservata (sessione di autenticazione, preferenze di consenso). Non richiedono consenso.",
      "Cookie di analisi: utilizzati in forma aggregata e anonima per statistiche di navigazione, esclusivamente previo consenso espresso tramite il banner cookie. Potete modificare la scelta in qualsiasi momento cancellando i dati di navigazione del browser.",
      "Cookie di terze parti: il sito può incorporare contenuti esterni (ad esempio immagini e font); tali servizi possono installare propri cookie secondo le rispettive informative.",
    ],
  },
];

const SECTIONS_EN = [
  {
    title: "1. Data Controller",
    body: [
      "The data controller is Mediolanum Consulting Srl, with registered office at Via Card. Branda Castiglioni 2/1, 20156 Milan (MI), Italy, VAT / Tax Code 14419200960, Milan Chamber of Commerce REA MI-2781751.",
      "Controller contacts: email info@mediolanumconsulting.com — certified email (PEC) mediocons@pec.it — Tel. +39 0235983003.",
    ],
  },
  {
    title: "2. Data collected and purposes",
    body: [
      "Voluntarily provided data: through the contact form and the newsletter subscription form we collect name, company name, email, phone number and the content of the message. Such data are processed exclusively to respond to the enquiry, for the possible establishment of a professional relationship and — only if requested — for sending the Mediolanum Financial Review newsletter.",
      "Reserved Area data: for the firm's clients we process access credentials, uploaded documents and technical metadata, solely to provide the reserved document portal service.",
      "Browsing data: the IT systems acquire certain technical data (IP address, browser type, access time) necessary for the operation and security of the website.",
    ],
  },
  {
    title: "3. Legal basis of processing",
    body: [
      "Processing is based on the performance of pre-contractual and contractual measures (Art. 6(1)(b) GDPR), compliance with legal obligations (Art. 6(1)(c) GDPR), the controller's legitimate interest in system security (Art. 6(1)(f) GDPR) and, for the newsletter and non-technical cookies, on the data subject's consent (Art. 6(1)(a) GDPR), which may be withdrawn at any time.",
    ],
  },
  {
    title: "4. Retention and security",
    body: [
      "Data are retained for the time strictly necessary for the stated purposes and in any case according to the legal terms applicable to professional records. Contact enquiries are retained for a maximum of 24 months; Reserved Area documents for the duration of the professional relationship.",
      "We adopt appropriate technical and organisational measures: encrypted connections (HTTPS), credentials protected with hashing algorithms, reserved and tracked document access, internal confidentiality protocols.",
    ],
  },
  {
    title: "5. Disclosure to third parties",
    body: [
      "Data are not sold or transferred to third parties for marketing purposes. They may be disclosed exclusively to technical providers acting as data processors (hosting, transactional email services) and, at the client's express request, to professionals involved in the engagement (notaries, consultants, credit institutions).",
    ],
  },
  {
    title: "6. Rights of the data subject",
    body: [
      "Under Articles 15-22 of Regulation (EU) 2016/679 you have the right of access, rectification, erasure, restriction, portability and objection to processing, as well as the right to withdraw any consent given.",
      "To exercise your rights, write to info@mediolanumconsulting.com or to the certified email mediocons@pec.it. You also have the right to lodge a complaint with the Italian Data Protection Authority (www.garanteprivacy.it).",
    ],
  },
  {
    title: "7. Cookie Policy",
    body: [
      "Technical cookies (necessary): indispensable for the operation of the website and the Reserved Area (authentication session, consent preferences). They do not require consent.",
      "Analytics cookies: used in aggregate and anonymous form for browsing statistics, exclusively subject to express consent via the cookie banner. You can change your choice at any time by clearing your browser's browsing data.",
      "Third-party cookies: the site may embed external content (e.g. images and fonts); such services may install their own cookies according to their respective policies.",
    ],
  },
];

const TEXT = {
  it: {
    seoTitle: "Privacy & Cookie Policy | Mediolanum Consulting Srl",
    seoDesc: "Informativa sul trattamento dei dati personali ai sensi del GDPR e cookie policy di Mediolanum Consulting Srl, Milano.",
    eyebrow: "Documento Legale",
    copy: "Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679 (GDPR) e della normativa italiana vigente. Ultimo aggiornamento: ottobre 2026.",
  },
  en: {
    seoTitle: "Privacy & Cookie Policy | Mediolanum Consulting Srl",
    seoDesc: "Information on the processing of personal data under the GDPR and cookie policy of Mediolanum Consulting Srl, Milan.",
    eyebrow: "Legal Document",
    copy: "Information on the processing of personal data under Regulation (EU) 2016/679 (GDPR) and applicable Italian law. Last updated: October 2026.",
  },
};

export default function Privacy() {
  const { lang } = useLang();
  const c = TEXT[lang];
  const sections = lang === "en" ? SECTIONS_EN : SECTIONS_IT;

  return (
    <main data-testid="privacy-page">
      <Seo title={c.seoTitle} description={c.seoDesc} />
      <section className="navy-mesh mx-auto max-w-4xl px-5 pb-10 pt-44 sm:px-8">
        <SectionHeading
          level={1}
          eyebrow={c.eyebrow}
          title={<>Privacy &amp; <span className="text-gold-gradient italic">Cookie Policy</span></>}
          copy={c.copy}
        />
      </section>

      <section className="mx-auto max-w-4xl px-5 pb-28 sm:px-8">
        <div className="space-y-6">
          {sections.map((s, i) => (
            <Reveal key={s.title} delay={Math.min(i * 0.05, 0.3)}>
              <div className="card-surface rounded-2xl p-7 sm:p-9" data-testid={`privacy-section-${i}`}>
                <h2 className="font-serif text-xl text-slate-100 sm:text-2xl">{s.title}</h2>
                <div className="mt-4 space-y-3">
                  {s.body.map((p, j) => (
                    <p key={j} className="text-sm font-light leading-relaxed text-slate-400">{p}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10">
          <p className="text-xs font-light leading-relaxed text-slate-500" data-testid="privacy-footer-note">
            Mediolanum Consulting Srl — Via Card. Branda Castiglioni 2/1, 20156 Milano (MI) — P.IVA / C.F. 14419200960 —
            REA MI-2781751 — Cap. Soc. € 10.000,00 — PEC mediocons@pec.it
          </p>
        </Reveal>
      </section>
    </main>
  );
}
